import PropTypes from 'prop-types';

import Container, { ContainerProps as ContainerProps } from '@mui/material/Container';
import { styled } from '@mui/material/styles';
import { Box, Link, Stack, Typography } from '@mui/material';
import { Button } from '@classity-edtech/ui-base';
import { useConfig } from '../../../contexts';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { LinkedInIcon } from '@classity-edtech/ui-base/dist/components/Displays';
import ExternalLink from '../../../components/ExternalLink';
import MarkDownComp from '../../../components/MarkDownComp';

export interface FeaturesProps extends ContainerProps {
  contents: {
    title: string;
    description: string;
    items: {
      title: string;
      cta: string;
      descriptions: string[];
      imageUrl: string;
    }[];
  };
  showLinkedInLink?: boolean;
  hideHeader?: boolean;
  isExecutives?: boolean;
}

export const Features = ({
  className,
  contents,
  showLinkedInLink,
  hideHeader,
  isExecutives,
  ...props
}: FeaturesProps) => {
  const screens = useAppMediaQuery();

  const { learnUi } = useConfig() as any;
  const learnUiUrl = learnUi?.url;
  const smallScreens = screens.isMobile || screens.isTablet;

  return (
    <Container
      className={className}
      sx={{
        minHeight: { xs: 'max-content', md: '100vh' },
        display: 'flex',
        flexDirection: 'column',
        gap: 9,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {!hideHeader && (
        <Stack sx={{ maxWidth: '500px' }}>
          <Typography
            variant='subtitle1'
            sx={{ textAlign: 'center' }}
            variantMapping={{ subtitle1: 'h2' }}
          >
            {contents?.title}
          </Typography>
          <Typography variant='body1' sx={{ textAlign: 'center' }}>
            {contents?.description}
          </Typography>
        </Stack>
      )}

      {contents?.items?.map((section, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexDirection: { xs: 'column-reverse', md: i % 2 === 0 ? 'row-reverse' : 'row' },
          }}
        >
          <Box>
            <picture>
              <source media='(min-width:768px)' srcSet={section.imageUrl} />
              <img
                src={section.imageUrl}
                alt=''
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'block',
                  objectFit: 'cover',
                }}
              />
            </picture>
          </Box>
          <Stack
            sx={{
              flex: 0.5,
              aspectRatio: { xs: 'none', md: '1 / 1' },
              gap: 2,
              p: 3,
              justifyContent: 'center',
            }}
          >
            <Box>
              <Typography
                variant={screens.isDesktop ? 'subtitle2' : 'subtitle3'}
                sx={{
                  minWidth: { xs: '100%', md: '400px' },
                  display: 'block',
                }}
              >
                {section.title}
              </Typography>
              {isExecutives && (
                <Typography variant='subtitle4' sx={{ display: 'block' }}>
                  {section?.descriptions?.[0]}
                </Typography>
              )}
            </Box>
            {section?.descriptions?.slice(isExecutives ? 1 : 0)?.map((d, index) => (
              <Typography
                key={index}
                variant={screens.isDesktop ? 'body2' : 'body3'}
                sx={{
                  minWidth: { xs: '100%', md: '400px' },
                }}
              >
                <MarkDownComp content={d} />
              </Typography>
            ))}
            {showLinkedInLink ? (
              <ExternalLink
                cta={section?.cta}
                icon={<LinkedInIcon sx={{ width: '24px', height: '24px' }} />}
              />
            ) : (
              <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
                <Link
                  href={`${learnUiUrl}/teach/signup`}
                  sx={{ textDecoration: 'none', width: 'fit-content' }}
                >
                  <Button
                    variant='outlined'
                    sx={{
                      px: { xs: 1.5, md: 3 },
                      py: { xs: '5px', md: 1.5 },
                      '&:hover': {
                        background: globalColours['$global-colour-interactive-accent-default'],
                        color: globalColours['$global-colour-base-contrast'],
                      },
                    }}
                  >
                    <Typography variant={smallScreens ? 'body3SemiBold' : 'body2SemiBold'}>
                      {section.cta}
                    </Typography>
                  </Button>
                </Link>
              </Box>
            )}
          </Stack>
        </Box>
      ))}
    </Container>
  );
};

Features.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

Features.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(Features)(
  ({ theme }) => `
  &.MuiBox-root {
  }
`,
);
